import { useState } from "react";
import { useNavigate } from "react-router-dom";
import SEO from "../SEO/SEO";

const ProductsSection = () => {
  const products = [
    {
      id: 1,
      name: "Electrical Tapping Machine",
      image: "/OurProduct/tappingMaachine.png",
      category: "Precision Tools",
      shortDesc: "High-precision electrical tapping machine for threaded holes",
      specs: ["Max Tap Size: M12", "Speed: 50-3000 RPM", "Accuracy: ±0.01mm"],
      priceRange: "Custom Quote",
      featured: true,
    },
    {
      id: 2,
      name: "Vertical Turret Machine",
      image: "/OurProduct/images.png",
      category: "Milling",
      shortDesc: "5-axis vertical turret milling machine",
      specs: ["Table Size: 1370x254mm", "Spindle: 5 HP", "Auto Lubrication"],
      priceRange: "Custom Quote",
      featured: true,
    },
    {
      id: 3,
      name: "VMC Machine",
      image: "/OurProduct/VMC.png",
      category: "VMC",
      shortDesc: "Vertical Machining Center with CNC control",
      specs: ["Travel: 800x500x500mm", "Spindle: 7.5kW", "BT-40 Tooling"],
      priceRange: "Custom Quote",
      featured: true,
    },
    {
      id: 4,
      name: "High Precision Machinery",
      image: "/OurProduct/accuracy.jpg",
      category: "Automation",
      shortDesc: "Automated precision machinery solutions",
      specs: [
        "Repeatability: ±0.005mm",
        "PLC Controlled",
        "Custom Configurable",
      ],
      priceRange: "Custom Quote",
      featured: true,
    },
  ];

  const [selectedProduct, setSelectedProduct] = useState(null);

  const handleViewDetails = (product) => {
    setSelectedProduct(product);
  };

  const handleInquiry = (productName) => {
    window.location.href = `/contact?product=${encodeURIComponent(productName)}`;
  };
  const navigate = useNavigate();

  const handleConsultation = () => {
    navigate("/contact");
  };
  const handleCatalog = () => {
    navigate("/Catloge");
  };

  return (
    <section className="bg-gray-50 py-12 sm:py-16 px-4 sm:px-6">
      <SEO
        title="Products | Nexora Machinery"
        description="Explore CNC Turning Centers, VMC Machines, Milling Machines, Tapping Machines and Industrial Machinery."
        keywords="CNC Machines, Tapping Machine, Milling Machines, Lathe, Drilling machines"
        path="/products"
      />
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-12 sm:mb-16">
          <div className="inline-flex items-center gap-2 bg-white rounded-full px-4 py-2 mb-4 border border-gray-200 shadow-sm">
            <div className="w-1.5 h-1.5 bg-[#00d9ff] rounded-full"></div>
            <span className="text-sm font-semibold text-[#002946]">
              PREMIUM INDUSTRIAL SOLUTIONS
            </span>
          </div>
          <h1 className="text-3xl sm:text-4xl md:text-5xl font-bold text-[#002946] mb-4">
            Our <span className="text-[#00d9ff]">Product Range</span>
          </h1>
          <p className="text-gray-600 max-w-3xl mx-auto text-lg">
            Cutting-edge industrial machinery engineered for performance,
            precision, and reliability in manufacturing
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6 sm:gap-7 mb-16">
          {products.map((product) => (
            <div key={product.id} className="group relative">
              {/* Subtle blue/teal glow */}
              <div className="absolute -inset-[1px] rounded-[24px] bg-gradient-to-br from-blue-400/20 via-white to-teal-400/20 blur-[2px] opacity-70 group-hover:opacity-100 transition-opacity duration-500" />

              {/* Main Card */}
              <div className="relative bg-white/95 backdrop-blur-xl rounded-[24px] border border-slate-200/80 overflow-hidden shadow-[0_12px_40px_rgba(15,23,42,0.08)] group-hover:shadow-[0_20px_55px_rgba(15,23,42,0.12)] group-hover:-translate-y-1 transition-all duration-500">
                {/* ================= IMAGE ================= */}
                <div className="relative h-48 sm:h-52 bg-gradient-to-br from-slate-50 via-white to-blue-50/30 overflow-hidden">
                  {/* Soft image glow */}
                  <div className="absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 w-36 h-36 bg-blue-400/10 blur-[50px] rounded-full" />

                  <img
                    src={product.image}
                    alt={product.name}
                    className="relative z-10 w-full h-full object-contain p-5 transition-transform duration-500 group-hover:scale-[1.04]"
                    loading="lazy"
                  />

                  {/* Category */}
                  <div className="absolute top-3.5 left-3.5 z-20">
                    <span className="inline-flex items-center gap-1.5 bg-white/95 backdrop-blur-md border border-slate-200 text-slate-600 text-[10px] font-bold uppercase tracking-wide px-3 py-1.5 rounded-full shadow-sm">
                      <span className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                      {product.category}
                    </span>
                  </div>

                  {/* Featured */}
                  {product.featured && (
                    <div className="absolute top-3.5 right-3.5 z-20">
                      <span className="inline-flex items-center gap-1.5 bg-slate-900 text-white text-[10px] font-bold uppercase tracking-wide px-3 py-1.5 rounded-full shadow-md">
                        <span className="w-1.5 h-1.5 rounded-full bg-teal-400" />
                        Featured
                      </span>
                    </div>
                  )}
                </div>

                {/* ================= CONTENT ================= */}
                <div className="p-5">
                  {/* Product Name */}
                  <h3 className="font-bold text-slate-900 text-base leading-snug mb-2 line-clamp-2 group-hover:text-blue-600 transition-colors duration-300">
                    {product.name}
                  </h3>

                  {/* Description */}
                  <p className="text-slate-500 text-xs sm:text-sm leading-5 mb-4 line-clamp-2">
                    {product.shortDesc}
                  </p>

                  {/* Specifications */}
                  <div className="mb-4 space-y-1.5">
                    {product.specs.slice(0, 2).map((spec, idx) => (
                      <div
                        key={idx}
                        className="flex items-start gap-2 text-[11px] sm:text-xs text-slate-500"
                      >
                        <span className="w-1.5 h-1.5 mt-1.5 shrink-0 rounded-full bg-blue-500" />

                        <span className="line-clamp-1">{spec}</span>
                      </div>
                    ))}
                  </div>

                  {/* Price */}
                  <div className="mb-4 pt-3 border-t border-slate-100">
                    <span className="text-slate-900 font-bold text-sm">
                      {product.priceRange}
                    </span>
                  </div>

                  {/* Buttons */}
                  <div className="flex gap-2">
                    {/* Details */}
                    <button
                      onClick={() => handleViewDetails(product)}
                      className="flex-1 flex items-center justify-center gap-1.5 border border-slate-200 text-slate-700 py-2.5 rounded-xl font-semibold text-xs hover:border-blue-500 hover:text-blue-600 hover:bg-blue-50 transition-all duration-300 active:scale-95"
                    >
                      Details
                      <span className="text-sm transition-transform duration-300 group-hover:translate-x-0.5">
                        →
                      </span>
                    </button>

                    {/* Inquiry */}
                    <button
                      onClick={() => handleInquiry(product.name)}
                      className="flex-1 flex items-center justify-center gap-1.5 bg-slate-900 text-white py-2.5 rounded-xl font-semibold text-xs hover:bg-blue-600 transition-all duration-300 active:scale-95 shadow-sm"
                    >
                      Inquiry
                    </button>
                  </div>
                </div>

                {/* Bottom subtle accent */}
                <div className="absolute bottom-0 left-8 right-8 h-px bg-gradient-to-r from-transparent via-blue-300/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
              </div>
            </div>
          ))}
        </div>

        <div className="text-center relative">
          <div
            className="
      absolute -inset-3
      rounded-[32px]
      bg-gradient-to-r
      from-blue-500/[0.08]
      via-transparent
      to-teal-500/[0.08]
      blur-2xl
    "
          />
          <div
            className="
      relative
      max-w-6xl
      mx-auto
      overflow-hidden
      rounded-[28px]
      border
      border-slate-200/60
      bg-gradient-to-br
      from-slate-900
      via-[#062d47]
      to-[#073f5c]

      shadow-[0_3px_8px_rgba(15,23,42,0.04),0_15px_35px_rgba(15,23,42,0.10),0_35px_80px_rgba(15,23,42,0.12)]

      px-7
      py-9
      sm:px-12
      sm:py-11
    "
          >
            <div
              className="
        absolute
        -top-32
        -right-24
        w-80
        h-80
        rounded-full
        bg-blue-500/10
        blur-[90px]
        pointer-events-none
      "
            />

            <div
              className="
        absolute
        -bottom-32
        -left-24
        w-80
        h-80
        rounded-full
        bg-teal-400/10
        blur-[90px]
        pointer-events-none
      "
            />
            <div className="relative z-10">
              {/* Small label */}

              <div className="inline-flex items-center gap-2 mb-4">
                <span className="w-1.5 h-1.5 rounded-full bg-teal-400" />

                <span
                  className="
            text-[10px]
            sm:text-[11px]
            font-bold
            uppercase
            tracking-[0.18em]
            text-blue-200
          "
                >
                  Custom Industrial Solutions
                </span>
              </div>

              {/* Heading */}

              <h3
                className="
          text-2xl
          sm:text-3xl
          lg:text-4xl
          font-extrabold
          tracking-tight
          text-white
          mb-3
        "
              >
                Need Custom Industrial Solutions?
              </h3>

              {/* Description */}

              <p
                className="
          text-slate-300
          text-sm
          sm:text-base
          leading-6
          mb-7
          max-w-2xl
          mx-auto
        "
              >
                We provide tailored machinery solutions for your specific
                manufacturing requirements.
              </p>
              <div
                className="
          flex
          flex-col
          sm:flex-row
          gap-3
          justify-center
          items-center
        "
              >
                <button
                  onClick={handleConsultation}
                  className="
            w-full
            sm:w-auto

            inline-flex
            items-center
            justify-center
            gap-2

            bg-white
            text-slate-900

            px-7
            py-3.5

            rounded-xl

            font-bold
            text-sm

            border
            border-white/80

            shadow-[0_6px_20px_rgba(0,0,0,0.15)]

            hover:bg-blue-50
            hover:-translate-y-0.5
            hover:shadow-[0_10px_25px_rgba(0,0,0,0.20)]

            active:scale-95

            transition-all
            duration-300
          "
                >
                  Get Free Consultation
                  <span className="text-blue-600 text-base">→</span>
                </button>
                <button
                  onClick={handleCatalog}
                  className="
            w-full
            sm:w-auto

            inline-flex
            items-center
            justify-center
            gap-2

            bg-white/5
            backdrop-blur-sm

            border
            border-white/25

            text-white

            px-7
            py-3.5

            rounded-xl

            font-bold
            text-sm

            hover:bg-white
            hover:text-slate-900
            hover:border-white

            active:scale-95

            transition-all
            duration-300
          "
                >
                  Download Catalog
                  <span className="text-sm">↓</span>
                </button>
              </div>
              <div className="flex items-center justify-center gap-2 mt-5">
                <span className="w-1.5 h-1.5 rounded-full bg-emerald-400" />

                <p className="text-slate-400 text-xs">
                  Quick response within 2 hours
                </p>
              </div>
            </div>
            <div
              className="
        absolute
        bottom-0
        left-16
        right-16
        h-px
        bg-gradient-to-r
        from-transparent
        via-blue-400/40
        to-transparent
      "
            />
          </div>
        </div>
        {selectedProduct && (
          <ProductModal
            product={selectedProduct}
            onClose={() => setSelectedProduct(null)}
            onInquiry={handleInquiry}
          />
        )}
      </div>
    </section>
  );
};

const ProductModal = ({ product, onClose, onInquiry }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-2xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="relative">
          <div className="flex justify-between items-center p-6 border-b">
            <h2 className="text-2xl font-bold text-[#002946]">
              {product.name}
            </h2>
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 text-2xl"
            >
              ×
            </button>
          </div>
          <div className="p-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              <div className="bg-gray-100 rounded-lg p-4">
                <img
                  src={product.image}
                  alt={product.name}
                  className="w-full h-64 object-contain rounded-lg"
                />
              </div>
              <div>
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-[#002946] mb-3">
                    Description
                  </h3>
                  <p className="text-gray-700">{product.shortDesc}</p>
                </div>
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-[#002946] mb-3">
                    Specifications
                  </h3>
                  <div className="space-y-2">
                    {product.specs.map((spec, idx) => (
                      <div key={idx} className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-[#00d9ff] rounded-full"></div>
                        <span className="text-gray-700">{spec}</span>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="mb-6">
                  <h3 className="text-lg font-semibold text-[#002946] mb-2">
                    Price Range
                  </h3>
                  <p className="text-xl font-bold text-[#002946]">
                    {product.priceRange}
                  </p>
                </div>
                <button
                  onClick={() => onInquiry(product.name)}
                  className="w-full bg-[#002946] text-white py-3 rounded-lg font-semibold hover:bg-[#004e7c] transition-colors duration-200"
                >
                  Request Quote
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProductsSection;
